import React, { useState } from 'react';
import Icon from '@mdi/react';
import { mdiClose, mdiCalendarPlusOutline, mdiFood, mdiCloseCircleOutline, mdiCalendarExportOutline } from '@mdi/js';
import CustomFormInput from './forms/CustomFormInput';
import TimeSelectDropdown from './TimeSelectDropdown';
import './WeeklyScheduleModal.css'; // shared styles: .weekly-day-*, .slot-glyph, .weekly-break-modal-*

/**
 * WeeklyDayRows — the Google-calendar-style per-day availability editor, shared
 * by WeeklyScheduleModal (create), EditWeeklyScheduleModal (edit), and
 * TravelAvailabilitySetupModal (travel — date rows instead of weekdays).
 *
 * One row per day. A day that's on has one or more *slot groups* (each a
 * start–end range) and an optional list of day-wide *breaks* (start + duration,
 * carved out of availability). Renders the add-group / add-break icons, the
 * per-group slot counts, the day-summary glyph, and its own Add-break modal.
 *
 * State contract: the parent owns `dayConfig` (see makeDay) and passes
 * `setDayConfig`; all group/break CRUD happens here.
 *
 * Props:
 *   dayConfig / setDayConfig / duration — as above
 *   days      — optional [{ key, label }] row list; defaults to the 7 weekdays
 *               (home). Travel passes trip dates (key = ISO date, label =
 *               "Sat 1 Aug") and keys its dayConfig by ISO date.
 *   variant   — 'turquoise' (home, default) | 'purple' (travel); themes the
 *               checkboxes, action icons, glyph, and break modal.
 *   onCopyDay — optional (key) => void; renders a copy icon on configured
 *               primary rows (travel's copy-to-other-days flow).
 */

export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
export const DEFAULT_DAY_START = '09:00';
export const DEFAULT_DAY_END = '17:00';

export const makeDay = (enabled) => ({
    enabled,
    groups: [{ startTime: DEFAULT_DAY_START, endTime: DEFAULT_DAY_END }],
    breaks: [], // [{ startTime: 'HH:MM', durationMins: number }]
});

/** "HH:MM" (24h) → minutes since midnight. */
export function timeStrToMinutes(hhmm) {
    const [h, m] = (hhmm || '00:00').split(':').map(Number);
    return h * 60 + m;
}

/** Minutes since midnight → "HH:MM" (24h), clamped to the day. */
export function minutesToTimeStr(mins) {
    const clamped = Math.max(0, Math.min(mins, 24 * 60 - 1));
    const h = Math.floor(clamped / 60);
    const m = clamped % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Serialize the per-day config to the backend's windows[] shape.
 * A break needs no schema of its own: each group is split at every break it
 * spans, emitting one window per contiguous run of available time. Two groups
 * on one day = two windows on the same dayOfWeek (fully supported by the
 * recurring_availability schema).
 */
export function buildWindows(dayConfig) {
    const windows = [];
    DAY_NAMES.forEach((day, dayIdx) => {
        const cfg = dayConfig[day];
        if (!cfg.enabled) return;
        const breakIntervals = cfg.breaks
            .map(b => ({ start: timeStrToMinutes(b.startTime), end: timeStrToMinutes(b.startTime) + b.durationMins }))
            .sort((a, b) => a.start - b.start);
        cfg.groups.forEach(g => {
            let segStart = timeStrToMinutes(g.startTime);
            const end = timeStrToMinutes(g.endTime);
            breakIntervals.forEach(bi => {
                if (bi.end <= segStart || bi.start >= end) return; // break outside this segment
                if (bi.start > segStart) {
                    windows.push({ dayOfWeek: dayIdx, startTime: minutesToTimeStr(segStart), endTime: minutesToTimeStr(bi.start) });
                }
                segStart = Math.max(segStart, bi.end);
            });
            if (segStart < end) {
                windows.push({ dayOfWeek: dayIdx, startTime: minutesToTimeStr(segStart), endTime: minutesToTimeStr(end) });
            }
        });
    });
    return windows;
}

/**
 * Reverse mapping for the EDIT modal: stored windows[] → per-day config.
 *
 * A break is persisted as a gap between same-day windows, so gaps are
 * re-interpreted on load: a gap of ≤ BREAK_GAP_MAX_MINS between consecutive
 * windows reconstructs as a BREAK row (the windows merge into one group
 * spanning it); a larger gap means genuinely separate slot groups. Either
 * representation serializes back to identical windows via buildWindows, so
 * the round-trip is exact.
 */
const BREAK_GAP_MAX_MINS = 90; // matches the largest offerable break duration
export function windowsToDayConfig(windows = []) {
    const config = {};
    DAY_NAMES.forEach((day, idx) => {
        const dayWindows = windows
            .filter(w => w.dayOfWeek === idx)
            .sort((a, b) => timeStrToMinutes(a.startTime) - timeStrToMinutes(b.startTime));
        if (dayWindows.length === 0) {
            config[day] = makeDay(false);
            return;
        }
        const groups = [];
        const breaks = [];
        let current = { startTime: dayWindows[0].startTime, endTime: dayWindows[0].endTime };
        dayWindows.slice(1).forEach(w => {
            const gap = timeStrToMinutes(w.startTime) - timeStrToMinutes(current.endTime);
            if (gap > 0 && gap <= BREAK_GAP_MAX_MINS) {
                // Break-sized gap → a break row; the group extends across it
                breaks.push({ startTime: current.endTime, durationMins: gap });
                current.endTime = w.endTime;
            } else if (gap <= 0) {
                // Touching/overlapping windows → merge seamlessly
                if (timeStrToMinutes(w.endTime) > timeStrToMinutes(current.endTime)) {
                    current.endTime = w.endTime;
                }
            } else {
                // Big gap → a genuinely separate slot group
                groups.push(current);
                current = { startTime: w.startTime, endTime: w.endTime };
            }
        });
        groups.push(current);
        config[day] = { enabled: true, groups, breaks };
    });
    return config;
}

/**
 * Compute a day's timeline as glyph items: one 'slot' per bookable lesson, one
 * 'break' where a break interval interrupts the run. Mirrors the lunch-break
 * exclusion rule — a slot that would overlap a break is skipped and the cursor
 * jumps to the break's end. Breaks in the gap between groups still appear.
 */
export function computeDayGlyphItems(cfg, durationMins) {
    const breakIntervals = cfg.breaks
        .map(b => ({ start: timeStrToMinutes(b.startTime), end: timeStrToMinutes(b.startTime) + b.durationMins }))
        .sort((a, b) => a.start - b.start);
    const usedBreaks = new Set();
    const items = [];
    const groups = [...cfg.groups].sort((a, b) => timeStrToMinutes(a.startTime) - timeStrToMinutes(b.startTime));
    groups.forEach(g => {
        let cur = timeStrToMinutes(g.startTime);
        const end = timeStrToMinutes(g.endTime);
        while (cur + durationMins <= end) {
            const hit = breakIntervals.find(bi => cur < bi.end && cur + durationMins > bi.start);
            if (hit) {
                const key = `${hit.start}-${hit.end}`;
                if (!usedBreaks.has(key)) {
                    usedBreaks.add(key);
                    items.push({ type: 'break', start: hit.start });
                }
                cur = hit.end;
            } else {
                items.push({ type: 'slot', start: cur });
                cur += durationMins;
            }
        }
    });
    // A break that no slot ran into (e.g. sitting in the gap between two groups)
    // still belongs on the day's timeline — add it, then sort chronologically.
    breakIntervals.forEach(bi => {
        const key = `${bi.start}-${bi.end}`;
        if (!usedBreaks.has(key)) items.push({ type: 'break', start: bi.start });
    });
    items.sort((a, b) => a.start - b.start);
    return items;
}

/**
 * Serialize ONE day's config to concrete bookable slots — the shape the
 * availability_overrides endpoints take ({ startTime, endTime, slotDuration }).
 * Same walk as the glyph: consecutive slots per group, skipping breaks.
 */
export function dayConfigToSlots(cfg, durationMins) {
    return computeDayGlyphItems(cfg, durationMins)
        .filter(item => item.type === 'slot')
        .map(item => ({
            startTime: minutesToTimeStr(item.start),
            endTime: minutesToTimeStr(item.start + durationMins),
            slotDuration: durationMins,
        }));
}

/**
 * Bookable slots within ONE group, honouring the day's breaks (a slot that would
 * overlap a break is skipped, resuming after it) — the per-row count label.
 */
function countGroupSlots(group, breaks, durationMins) {
    const breakIntervals = breaks.map(b => ({
        start: timeStrToMinutes(b.startTime),
        end: timeStrToMinutes(b.startTime) + b.durationMins,
    }));
    let count = 0;
    let cur = timeStrToMinutes(group.startTime);
    const end = timeStrToMinutes(group.endTime);
    while (cur + durationMins <= end) {
        const hit = breakIntervals.find(bi => cur < bi.end && cur + durationMins > bi.start);
        if (hit) {
            cur = hit.end;
        } else {
            count += 1;
            cur += durationMins;
        }
    }
    return count;
}

/**
 * SlotPreviewGlyph — mini day-summary rectangle: one turquoise line per bookable
 * slot, a purple line where a break interrupts. Grows with content. Pure derived
 * rendering — updates live as times / duration / breaks change.
 */
const SlotPreviewGlyph = ({ cfg, durationMins, variant = 'turquoise' }) => {
    const items = computeDayGlyphItems(cfg, durationMins);
    if (items.length === 0) return null;
    const slotCount = items.filter(i => i.type === 'slot').length;
    const breakCount = items.length - slotCount;
    const label = `${slotCount} slot${slotCount !== 1 ? 's' : ''}${breakCount ? `, ${breakCount} break${breakCount !== 1 ? 's' : ''}` : ''}`;
    return (
        <div className={`slot-glyph slot-glyph--${variant}`} aria-label={label}>
            {items.map((item, i) => (
                <span key={i} className={`slot-glyph-line${item.type === 'break' ? ' break' : ''}`} />
            ))}
        </div>
    );
};

/** A break → "12:00pm for 60 min" style label. */
function formatBreakLabel(brk) {
    const [h, m] = brk.startTime.split(':').map(Number);
    const period = h >= 12 ? 'pm' : 'am';
    const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${displayHour}:${String(m).padStart(2, '0')}${period} for ${brk.durationMins} min`;
}

/**
 * AddBreakModal — small sibling modal to add a day-wide break (start + duration).
 */
const BREAK_DURATION_OPTIONS = [15, 30, 45, 60, 90];
const AddBreakModal = ({ dayLabel, repeatsWeekly = true, variant = 'turquoise', onSave, onCancel }) => {
    const [startTime, setStartTime] = useState('12:00');
    const [durationMins, setDurationMins] = useState(60);

    return (
        <div className="weekly-break-modal-overlay" onClick={onCancel}>
            <div className={`weekly-break-modal weekly-break-modal--${variant}`} onClick={(e) => e.stopPropagation()}>
                <div className="weekly-break-modal-header">
                    <div className="weekly-break-modal-header-content">
                        <Icon
                            path={mdiFood}
                            size={1}
                            color={variant === 'purple' ? 'var(--dl-color-purple-default)' : 'var(--dl-color-turquoise-default)'}
                        />
                        <h3>Add a break</h3>
                    </div>
                    <button className="weekly-break-modal-close" onClick={onCancel}>
                        <Icon path={mdiClose} size={0.9} />
                    </button>
                </div>
                <div className="weekly-break-modal-body">
                    <p className="form-hint">
                        {repeatsWeekly
                            ? `A break is carved out of this day's availability, on every ${dayLabel} in the schedule.`
                            : `A break is carved out of the availability on ${dayLabel}.`}
                    </p>
                    <div className="weekly-break-fields">
                        <div className="form-group">
                            <label className="form-label">Start time</label>
                            <TimeSelectDropdown
                                value={startTime}
                                onChange={setStartTime}
                                accent={variant}
                                ariaLabel="Break start time"
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Duration</label>
                            <select
                                value={durationMins}
                                onChange={(e) => setDurationMins(parseInt(e.target.value, 10))}
                                className="duration-select">
                                {BREAK_DURATION_OPTIONS.map((d) => (
                                    <option key={d} value={d}>{d} minutes</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
                <div className="weekly-break-modal-footer">
                    <button className="btn-cancel" onClick={onCancel}>Cancel</button>
                    <button
                        className="btn-primary"
                        onClick={() => onSave({ startTime, durationMins })}>
                        Add break
                    </button>
                </div>
            </div>
        </div>
    );
};

const DEFAULT_WEEKDAY_ROWS = DAY_NAMES.map(d => ({ key: d, label: d.slice(0, 3) }));

const WeeklyDayRows = ({
    dayConfig,
    setDayConfig,
    duration,
    days = DEFAULT_WEEKDAY_ROWS,
    variant = 'turquoise',
    onCopyDay,
}) => {
    // Add-break modal — { day } when open (a break attaches to the whole day), else null.
    const [breakModal, setBreakModal] = useState(null);

    const toggleDay = (day) =>
        setDayConfig(prev => ({ ...prev, [day]: { ...prev[day], enabled: !prev[day].enabled } }));

    // Slot-group CRUD
    const setGroupTime = (day, groupIdx, field, value) =>
        setDayConfig(prev => ({
            ...prev,
            [day]: {
                ...prev[day],
                groups: prev[day].groups.map((g, i) => (i === groupIdx ? { ...g, [field]: value } : g)),
            },
        }));
    const addGroup = (day) =>
        setDayConfig(prev => {
            const d = prev[day];
            // New group defaults to a 60-min slot after everything already on the
            // day: the latest group end, bumped past any break it would land on.
            let start = Math.max(...d.groups.map(g => timeStrToMinutes(g.endTime)));
            const breakIntervals = d.breaks.map(b => ({
                start: timeStrToMinutes(b.startTime),
                end: timeStrToMinutes(b.startTime) + b.durationMins,
            }));
            let moved = true;
            while (moved) {
                moved = false;
                for (const bi of breakIntervals) {
                    if (start < bi.end && start + 60 > bi.start) {
                        start = bi.end;
                        moved = true;
                    }
                }
            }
            return {
                ...prev,
                [day]: {
                    ...d,
                    groups: [
                        ...d.groups,
                        { startTime: minutesToTimeStr(start), endTime: minutesToTimeStr(start + 60) },
                    ],
                },
            };
        });
    const removeGroup = (day, groupIdx) =>
        setDayConfig(prev => ({
            ...prev,
            [day]: { ...prev[day], groups: prev[day].groups.filter((_, i) => i !== groupIdx) },
        }));

    // Day-wide break CRUD (attaches to the whole day, multiple allowed)
    const addBreak = (day, brk) =>
        setDayConfig(prev => ({
            ...prev,
            [day]: { ...prev[day], breaks: [...prev[day].breaks, brk] },
        }));
    const removeBreak = (day, breakIdx) =>
        setDayConfig(prev => ({
            ...prev,
            [day]: { ...prev[day], breaks: prev[day].breaks.filter((_, i) => i !== breakIdx) },
        }));

    return (
        <>
            <div className={`weekly-day-rows weekly-day-rows--${variant}`}>
                {days.map(({ key, label }) => {
                    const cfg = dayConfig[key];
                    // Guard the one render frame where a changed `days` list can
                    // outrun the parent's dayConfig reset (e.g. trip dates changed).
                    if (!cfg) return null;
                    // Chronological rendering: the primary row shows the earliest
                    // group; every other group and each break renders as a sub-row
                    // sorted by start time (a 12pm break lists above a 1pm group).
                    const sortedGroups = cfg.groups
                        .map((g, idx) => ({ ...g, idx }))
                        .sort((a, b) => timeStrToMinutes(a.startTime) - timeStrToMinutes(b.startTime));
                    const primaryGroup = sortedGroups[0];
                    const subItems = [
                        ...sortedGroups.slice(1).map(g => ({
                            type: 'group',
                            start: timeStrToMinutes(g.startTime),
                            group: g,
                        })),
                        ...cfg.breaks.map((brk, idx) => ({
                            type: 'break',
                            start: timeStrToMinutes(brk.startTime),
                            brk,
                            idx,
                        })),
                    ].sort((a, b) => a.start - b.start);
                    return (
                        <div key={key} className={`weekly-day-block${cfg.enabled ? ' enabled' : ''}`}>
                            {/* Primary row: day toggle + earliest slot group (or "Unavailable") */}
                            <div className="weekly-day-row">
                                <div className="weekly-day-toggle">
                                    <CustomFormInput
                                        type="checkbox"
                                        variant={variant}
                                        layout="standard"
                                        label={label}
                                        checked={cfg.enabled}
                                        onChange={() => toggleDay(key)}
                                    />
                                </div>
                                {cfg.enabled ? (
                                    <>
                                        <div className="weekly-day-times">
                                            <TimeSelectDropdown
                                                value={primaryGroup.startTime}
                                                onChange={(v) => setGroupTime(key, primaryGroup.idx, 'startTime', v)}
                                                accent={variant}
                                                ariaLabel={`${label} start time`}
                                            />
                                            <span className="weekly-day-times-dash">–</span>
                                            <TimeSelectDropdown
                                                value={primaryGroup.endTime}
                                                onChange={(v) => setGroupTime(key, primaryGroup.idx, 'endTime', v)}
                                                accent={variant}
                                                ariaLabel={`${label} end time`}
                                            />
                                        </div>
                                        <div className="weekly-day-actions">
                                            <button
                                                type="button"
                                                className="weekly-day-action-btn weekly-day-break-btn"
                                                onClick={() => setBreakModal({ key, label })}
                                                title="Add a break e.g. lunch"
                                                aria-label={`Add a break on ${label}`}>
                                                <Icon path={mdiFood} size={0.85} />
                                            </button>
                                            <button
                                                type="button"
                                                className="weekly-day-action-btn"
                                                onClick={() => addGroup(key)}
                                                title="Add another slot group"
                                                aria-label={`Add another slot group on ${label}`}>
                                                <Icon path={mdiCalendarPlusOutline} size={0.85} />
                                            </button>
                                            {onCopyDay && (
                                                <button
                                                    type="button"
                                                    className="weekly-day-action-btn"
                                                    onClick={() => onCopyDay(key)}
                                                    title="Copy to other days"
                                                    aria-label={`Copy ${label} to other days`}>
                                                    <Icon path={mdiCalendarExportOutline} size={0.8} />
                                                </button>
                                            )}
                                        </div>
                                        {/* Per-group slot count */}
                                        <span className="weekly-group-count">
                                            {countGroupSlots(primaryGroup, cfg.breaks, duration)} slots
                                        </span>
                                        {/* Day summary glyph — one line per slot, contrasting line per break */}
                                        <SlotPreviewGlyph cfg={cfg} durationMins={duration} variant={variant} />
                                    </>
                                ) : (
                                    <span className="weekly-day-unavailable">Unavailable</span>
                                )}
                            </div>

                            {/* Sub-rows — remaining groups + breaks, in chronological order */}
                            {cfg.enabled &&
                                subItems.map(item =>
                                    item.type === 'group' ? (
                                        <div key={`group-${item.group.idx}`} className="weekly-day-row weekly-day-subrow">
                                            <div className="weekly-day-toggle" />
                                            <div className="weekly-day-times">
                                                <TimeSelectDropdown
                                                    value={item.group.startTime}
                                                    onChange={(v) => setGroupTime(key, item.group.idx, 'startTime', v)}
                                                    accent={variant}
                                                    ariaLabel={`${label} additional group start time`}
                                                />
                                                <span className="weekly-day-times-dash">–</span>
                                                <TimeSelectDropdown
                                                    value={item.group.endTime}
                                                    onChange={(v) => setGroupTime(key, item.group.idx, 'endTime', v)}
                                                    accent={variant}
                                                    ariaLabel={`${label} additional group end time`}
                                                />
                                            </div>
                                            <div className="weekly-day-actions">
                                                <button
                                                    type="button"
                                                    className="weekly-day-action-btn weekly-day-break-btn"
                                                    onClick={() => setBreakModal({ key, label })}
                                                    title="Add a break e.g. lunch"
                                                    aria-label={`Add a break on ${label}`}>
                                                    <Icon path={mdiFood} size={0.85} />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="weekly-day-action-btn"
                                                    onClick={() => addGroup(key)}
                                                    title="Add another slot group"
                                                    aria-label={`Add another slot group on ${label}`}>
                                                    <Icon path={mdiCalendarPlusOutline} size={0.85} />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="weekly-day-action-btn weekly-day-remove"
                                                    onClick={() => removeGroup(key, item.group.idx)}
                                                    title="Remove this slot group"
                                                    aria-label={`Remove this slot group on ${label}`}>
                                                    <Icon path={mdiCloseCircleOutline} size={0.85} />
                                                </button>
                                            </div>
                                            {/* Per-group slot count */}
                                            <span className="weekly-group-count">
                                                {countGroupSlots(item.group, cfg.breaks, duration)} slots
                                            </span>
                                        </div>
                                    ) : (
                                        <div key={`break-${item.idx}`} className="weekly-day-row weekly-day-breakrow">
                                            <div className="weekly-day-toggle" />
                                            <div className="weekly-day-break-label">
                                                <Icon path={mdiFood} size={0.7} color="var(--dl-color-gray-500)" />
                                                <span>
                                                    Break {formatBreakLabel(item.brk)}
                                                </span>
                                            </div>
                                            <div className="weekly-day-actions">
                                                <button
                                                    type="button"
                                                    className="weekly-day-action-btn weekly-day-remove"
                                                    onClick={() => removeBreak(key, item.idx)}
                                                    title="Remove this break"
                                                    aria-label={`Remove break on ${label}`}>
                                                    <Icon path={mdiCloseCircleOutline} size={0.85} />
                                                </button>
                                            </div>
                                        </div>
                                    )
                                )}
                        </div>
                    );
                })}
            </div>

            {/* Add-break modal — fixed-position overlay above the parent modal */}
            {breakModal && (
                <AddBreakModal
                    dayLabel={breakModal.label}
                    repeatsWeekly={variant === 'turquoise'}
                    variant={variant}
                    onSave={(brk) => {
                        addBreak(breakModal.key, brk);
                        setBreakModal(null);
                    }}
                    onCancel={() => setBreakModal(null)}
                />
            )}
        </>
    );
};

export default WeeklyDayRows;
